import { AxiosResponse } from 'axios';
import { useState, useEffect } from 'react';
import { Button, Form, Col } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import { CardTransaction } from './types';
import { modal } from '../modal/ModalEmitter';
import CardPayment from './CardPayment';
import PaymentCurrencyInput from './PaymentCurrencyInput';

interface Props {
  defaultAmount?: number;
  maxAmount: number;
  onError: (err: Error) => void;
  onRefund: ({
    refundTx,
    saleTx,
    voidTx,
  }: {
    refundTx?: CardTransaction;
    saleTx: CardTransaction;
    voidTx?: CardTransaction;
  }) => void;
  paymentId: string;
  sessionId?: string;
  userInitials?: string;
}

interface FormData {
  amount: string;
  userInitials: string;
}

const PaymentCardRefundForm = ({
  defaultAmount = 0,
  maxAmount,
  onError,
  onRefund,
  paymentId,
  sessionId = '',
  userInitials = '',
}: Props) => {
  const [payment, setPayment] = useState<CardPayment>();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    amount: String(defaultAmount),
    userInitials,
  });

  const handleFormData = (field: string, value: string) => {
    setFormData(old => {
      return { ...old, [field]: value };
    });
  };

  useEffect(() => {
    ApiService.get(`/payment/transaction/${paymentId}`).then((response: AxiosResponse) => {
      setPayment(new CardPayment(response.data));
    });
  }, []);

  if (!payment) {
    return null;
  }

  const handleRefund = () => {
    if (loading) {
      return;
    }

    setLoading(true);

    modal.confirm({
      message: `Refund $${formData.amount} back to card?`,
      onClose: () => setLoading(false),
      callback: () => {
        let request;
        if (payment.amount === parseFloat(formData.amount)) {
          // this is a full refund, attempt a void
          request = payment.doVoidOrRefundPayment({ userInitials, sessionId });
        } else {
          request = ApiService.post(`/payment/transaction/${paymentId}/refund`, {
            amount: parseFloat(formData.amount),
            userInitials: formData.userInitials,
            sessionId,
          });
        }

        request
          .then((response: AxiosResponse | undefined) => {
            if (!response) {
              throw new Error('Error while refunding');
            }
            const refundTx = response.data.refund;
            const saleTx = response.data.sale;
            const voidTx = response.data.void;
            onRefund({ refundTx, saleTx, voidTx });
          })
          .catch((err: Error) => {
            onError(err);
            console.error(err);
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };

  const handleAmountBlur = () => {
    handleFormData('amount', String(parseFloat(formData.amount || '0') > maxAmount ? maxAmount : formData.amount));
  };

  const isValidAmount = () => {
    const amount = parseFloat(formData.amount);
    return amount && amount <= parseFloat(maxAmount.toFixed(2));
  };

  const renderAction = () => {
    if (loading) {
      return (
        <Button variant="default" className="float-right" disabled>
          <i className="fas fa-circle-notch fa-spin" /> &nbsp; Processing
        </Button>
      );
    }

    return (
      <Button onClick={handleRefund} variant="warning" disabled={!isValidAmount()}>
        Refund
      </Button>
    );
  };

  return (
    <div className="d-flex flex-column flex-fill">
      <div className="flex-grow-1">
        <Form.Row>
          <Form.Group as={Col}>
            <Form.Label>Refund Amount</Form.Label>
            <PaymentCurrencyInput
              onChange={value => handleFormData('amount', value || '0')}
              onBlur={handleAmountBlur}
              onMax={() => handleFormData('amount', maxAmount.toFixed(2))}
              value={formData.amount}
            />
            <Form.Text>Max amount: ${maxAmount.toFixed(2)}</Form.Text>
          </Form.Group>
          {!userInitials && (
            <Form.Group as={Col}>
              <Form.Label>User Initials</Form.Label>
              <Form.Control
                type="text"
                value={userInitials}
                onChange={e => handleFormData('userInitials', e.target.value)}
              />
            </Form.Group>
          )}
          <Form.Group as={Col} xs="auto" className="align-self-center">
            {renderAction()}
          </Form.Group>
        </Form.Row>
      </div>
    </div>
  );
};

export default PaymentCardRefundForm;
